import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { WalletButton } from './WalletButton';
import { User } from '../lib/api';

interface LayoutProps {
  children: React.ReactNode;
  user: User | null;
  onAuthSuccess: (user: User) => void;
  onLogout: () => void;
}

export const Layout: React.FC<LayoutProps> = ({ children, user, onAuthSuccess, onLogout }) => {
  const [scrolled, setScrolled] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Navigation Bar */}
      <header className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="container navbar-inner">
          <Link to="/" className="navbar-logo">
            <span style={{ fontSize: '1.8rem' }}>🦶</span>
            <span className="gradient-text text-glow">SOLEMATE</span>
          </Link>

          <nav className="navbar-links">
            <NavLink 
              to="/explore" 
              className={({ isActive }) => `navbar-link ${isActive ? 'active' : ''}`}
            >
              Explore 👣
            </NavLink>
            
            {user?.is_creator === 1 && (
              <NavLink 
                to="/dashboard" 
                className={({ isActive }) => `navbar-link ${isActive ? 'active' : ''}`}
              >
                Dashboard 📈
              </NavLink>
            )}

            {user && user.is_creator === 0 && (
              <NavLink 
                to="/dashboard" 
                className={({ isActive }) => `navbar-link ${isActive ? 'active' : ''}`}
              >
                Become Creator 💎
              </NavLink>
            )}

            <WalletButton onAuthSuccess={onAuthSuccess} onLogout={onLogout} />
          </nav>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="container main-content" style={{ flex: 1 }}>
        {children}
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="container footer-inner">
          <div>
            <div className="navbar-logo" style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>
              <span>🦶</span>
              <span className="gradient-text">SOLEMATE</span>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              OnlyFans for Soles. The premium token-gated platform for sole content.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '2rem', fontSize: '0.85rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>PLATFORM</span>
              <Link to="/explore" style={{ color: 'var(--text-secondary)' }}>Explore Feed</Link>
              <Link to="/dashboard" style={{ color: 'var(--text-secondary)' }}>Creator Portal</Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>TOKEN</span>
              <a 
                href="https://pump.fun" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              >
                Buy on Pump.fun 💊
              </a>
              <a 
                href="https://jup.ag" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              >
                Trade on Jupiter 🪐
              </a>
            </div>
          </div>

          <div style={{ textAlign: 'right', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            <p>&copy; {new Date().getFullYear()} SOLEMATE. All rights reserved.</p>
            <p style={{ marginTop: '0.25rem' }}>Launched with 🦶 on Solana.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
